import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Check, ShieldCheck, Sparkles, ArrowUpRight, Percent, Crown } from "lucide-react";
import { toast } from "sonner";

export default function PricingPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  const subscribe = async (planKey) => {
    if (!user || user === false) {
      window.location.href = `/devenir-annonceur?plan=${planKey}`;
      return;
    }
    if (user.role !== "owner" && user.role !== "admin") {
      toast.error("Créez d'abord un compte annonceur");
      window.location.href = `/devenir-annonceur?plan=${planKey}`;
      return;
    }
    setBusy(planKey);
    try {
      await api.post("/owner/subscribe", { plan: planKey });
      toast.success("Abonnement activé (paiement simulé)");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy("");
    }
  };

  const planStyle = (key) => {
    if (key === "yearly")
      return {
        cardCls: "border-brand-gold shadow-[0_24px_60px_rgba(200,160,77,0.22)] bg-gradient-to-b from-white to-brand-cream",
        ctaCls: "bg-brand-gold hover:bg-brand-goldDark text-brand-black",
        accent: "text-brand-goldDark",
        Icon: Crown,
      };
    if (key === "monthly")
      return {
        cardCls: "border-brand-line",
        ctaCls: "bg-brand-terracotta hover:bg-brand-terracottaHover text-white",
        accent: "text-brand-terracotta",
        Icon: ShieldCheck,
      };
    return {
      cardCls: "border-brand-line",
      ctaCls: "bg-brand-ink hover:bg-brand-inkSoft text-white",
      accent: "text-brand-inkSoft",
      Icon: Percent,
    };
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-gold/15 text-brand-goldDark text-xs uppercase tracking-[0.2em] font-bold mb-5">
          <Sparkles className="w-3.5 h-3.5" />
          Tarifs annonceurs
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-brand-ink tracking-tight mb-4">
          Faites rayonner votre commerce sur Kanama
        </h1>
        <p className="text-brand-inkSoft leading-relaxed">
          Trois formules adaptées aux commerces du Tchad. La formule <strong>Annonce Basique</strong> est
          gratuite à la publication, avec une <strong>commission de 10 %</strong> prélevée sur le prix de
          chaque article vendu via Kanama. Pour ne payer aucune commission, choisissez un abonnement Premium :
          <strong> 50 000 FCFA / mois</strong> ou <strong>500 000 FCFA / an</strong>. Paiements Orange Money,
          Airtel Money et carte bancaire bientôt disponibles.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
        {plans.map((p) => {
          const isCurrent = user && user !== false && user.plan === p.key;
          const style = planStyle(p.key);
          const PlanIcon = style.Icon;
          const isYearly = p.key === "yearly";
          const isBasic = p.key === "basic";
          return (
            <Card
              key={p.key}
              data-testid={`plan-${p.key}`}
              className={`relative rounded-2xl border bg-white p-7 flex flex-col ${style.cardCls}`}
            >
              {isYearly && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand-gold text-brand-black text-[10px] font-bold uppercase tracking-wider shadow-md">
                  {p.savings_label || "Meilleure offre"}
                </div>
              )}
              <div className="mb-5">
                <div className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider mb-2 ${style.accent}`}>
                  <PlanIcon className="w-3.5 h-3.5" />
                  {p.name}
                </div>
                <p className="text-sm text-brand-inkMuted">{p.tagline}</p>
              </div>

              <div className="mb-2">
                {isBasic ? (
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="font-heading text-4xl font-bold text-brand-ink">0</span>
                    <span className="text-sm text-brand-inkMuted font-medium">FCFA à la publication</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="font-heading text-4xl font-bold text-brand-ink">
                      {p.price_xaf.toLocaleString("fr-FR")}
                    </span>
                    <span className="text-sm text-brand-inkMuted font-medium">FCFA / {p.period}</span>
                  </div>
                )}
              </div>

              {isBasic && (
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-terracotta bg-brand-terracotta/10 px-2.5 py-1 rounded-full self-start mb-5" data-testid="basic-commission-pill">
                  <Percent className="w-3 h-3" />
                  10 % de commission sur chaque article vendu
                </div>
              )}

              {isYearly && (
                <div className="text-xs text-brand-inkMuted mb-5" data-testid="yearly-savings-note">
                  Soit environ <strong className="text-brand-ink">41 667 FCFA / mois</strong> facturés annuellement · économisez{" "}
                  <strong className="text-brand-goldDark">100 000 FCFA</strong> par rapport au mensuel
                </div>
              )}

              {!isBasic && !isYearly && (
                <div className="text-xs text-brand-inkMuted mb-5" data-testid="monthly-billing-note">
                  Abonnement payant facturé chaque mois · activation immédiate
                </div>
              )}

              <ul className="space-y-2.5 mb-7 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-brand-ink">
                    <Check className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => subscribe(p.key)}
                disabled={busy === p.key || isCurrent}
                className={`w-full rounded-full ${style.ctaCls} font-semibold`}
                data-testid={`plan-cta-${p.key}`}
              >
                {isCurrent
                  ? "Votre plan actuel"
                  : busy === p.key
                  ? "Activation..."
                  : isBasic
                  ? "Publier avec 10 % de commission"
                  : `S'abonner — ${p.price_xaf.toLocaleString("fr-FR")} FCFA / ${p.period}`}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Trust band */}
      <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-sandlight flex items-center justify-center text-brand-gold shrink-0">
            <Percent className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-heading font-semibold text-brand-ink mb-1">Modèle de paiement clair</h4>
            <p className="text-sm text-brand-inkSoft leading-relaxed">
              Basique : <strong>0 FCFA</strong> à la publication + <strong>10 %</strong> sur les ventes.
              Premium : <strong>50 000 FCFA / mois</strong> ou <strong>500 000 FCFA / an</strong> sans aucune commission.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-sandlight flex items-center justify-center text-brand-gold shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-heading font-semibold text-brand-ink mb-1">Présent sur la carte du Tchad</h4>
            <p className="text-sm text-brand-inkSoft leading-relaxed">
              Vos annonces remontent en tête dans les 8 villes prioritaires et les 23 régions du pays.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-sandlight flex items-center justify-center text-brand-gold shrink-0">
            <ArrowUpRight className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-heading font-semibold text-brand-ink mb-1">Démarrez en formule Basique</h4>
            <p className="text-sm text-brand-inkSoft leading-relaxed">
              <Link to="/devenir-annonceur" className="text-brand-terracotta font-semibold hover:underline">
                Créez votre compte annonceur
              </Link>{" "}
              et publiez votre première annonce. Vous ne payez que <strong>10 %</strong> à la vente, jusqu'à passer en Premium.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ light */}
      <section className="mt-14 bg-brand-sandlight/40 border border-brand-line rounded-2xl p-7" data-testid="pricing-faq">
        <h3 className="font-heading text-xl font-semibold text-brand-ink mb-4">Comment fonctionne le modèle Basique + Premium ?</h3>
        <p className="text-sm text-brand-inkSoft leading-relaxed">
          La formule <strong>Annonce Basique</strong> est <strong>gratuite à la publication</strong> :
          publier votre annonce ne coûte rien à l'avance, mais Kanama prélève une <strong>commission de
          10 %</strong> sur le prix de chaque article vendu via la plateforme. Pour ne payer aucune
          commission et accéder à toutes les fonctionnalités, choisissez un abonnement Premium payant :
          <strong> 50 000 FCFA par mois</strong> ou <strong>500 000 FCFA par an</strong> (équivalent à
          environ 41 667 FCFA / mois, soit 2 mois offerts). Les deux formules Premium incluent les
          annonces illimitées, le badge vérifié et la visibilité prioritaire sur la carte du Tchad.
        </p>
      </section>

      <p className="text-center text-xs text-brand-inkMuted mt-10">
        ⚠️ Les paiements sont actuellement simulés en mode démo. L'intégration Stripe / Orange Money / Airtel Money est prévue prochainement.
      </p>
    </div>
  );
}
